/**
 * Where the workspace picker takes you (earlier settings spec §1.3, UX §2.2): the same page in the new workspace where
 * that makes sense, else its home. Pure, so the table is unit-tested.
 *
 * | You are on                                    | You land on                         |
 * | home                                          | home                                |
 * | a settings list page, an account page         | the same page                       |
 * | a runner's detail page, Pair a runner          | Settings › Runners                  |
 * | a project, anything else                      | home                                |
 *
 * Wave B2 (decision 0057): the account pages moved to `/w/$ws/account/*` (profile, security, apps); the old
 * `settings/account/profile|security` paths are aliases of the new ones.
 */
export type SwitchTarget =
  | { readonly to: "/w/$workspaceId" }
  | { readonly to: "/w/$workspaceId/account/profile" }
  | { readonly to: "/w/$workspaceId/account/security" }
  | { readonly to: "/w/$workspaceId/account/apps" }
  | { readonly to: "/w/$workspaceId/settings/general" }
  | { readonly to: "/w/$workspaceId/settings/members" }
  | { readonly to: "/w/$workspaceId/settings/runners" }
  | { readonly to: "/w/$workspaceId/settings/models" }

const SETTINGS_PAGES: Record<string, SwitchTarget["to"]> = {
  "account/profile": "/w/$workspaceId/account/profile",
  "account/security": "/w/$workspaceId/account/security",
  general: "/w/$workspaceId/settings/general",
  members: "/w/$workspaceId/settings/members",
  runners: "/w/$workspaceId/settings/runners",
  models: "/w/$workspaceId/settings/models",
}

const ACCOUNT_PAGES: Record<string, SwitchTarget["to"]> = {
  profile: "/w/$workspaceId/account/profile",
  security: "/w/$workspaceId/account/security",
  apps: "/w/$workspaceId/account/apps",
}

export const switchTarget = (pathname: string): SwitchTarget => {
  const account = /^\/w\/[^/]+\/account\/(.+?)\/?$/.exec(pathname)
  if (account?.[1]) {
    const to = ACCOUNT_PAGES[account[1]]
    return to ? { to } : { to: "/w/$workspaceId/account/security" }
  }
  const match = /^\/w\/[^/]+\/settings\/(.+?)\/?$/.exec(pathname)
  if (!match?.[1]) return { to: "/w/$workspaceId" }
  const rest = match[1]
  if (rest.startsWith("runners/")) return { to: "/w/$workspaceId/settings/runners" }
  const to = SETTINGS_PAGES[rest]
  return to ? { to } : { to: "/w/$workspaceId" }
}
